/**
 * Epoch handling and tick-label formatting. See implementation-plan.md §3.1.
 */

import type { BandUnit } from './types';

/**
 * "Before Present" reference year, per the source dataset's own legend
 * ("AP = Avant le Présent = Avant l'an 2000"). This departs from the scientific
 * convention (BP = 1950) deliberately, and is confined here so it stays
 * changeable in one character.
 */
export const EPOCH_BP = 2000;

export function yearToBP(year: number): number {
  return EPOCH_BP - year;
}

export function bpToYear(bp: number): number {
  return EPOCH_BP - bp;
}

const YEARS_BP = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
const MA_BP = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });

const UNIT_SUFFIX: Record<BandUnit, string> = { year: '', yearBP: ' ans AP', MaBP: ' Ma AP' };

/**
 * The number alone, in the band's scale. Used where the unit would only take up
 * room — an event marker sits right above an axis that already states it.
 */
export function formatYearValue(year: number, unit: BandUnit = 'year'): string {
  switch (unit) {
    case 'year':
      return String(year);
    case 'yearBP':
      return YEARS_BP.format(yearToBP(year));
    case 'MaBP':
      return MA_BP.format(yearToBP(year) / 1_000_000);
  }
}

/** A tick label: the value plus its unit, which is what carries the scale. */
export function formatYear(year: number, unit: BandUnit = 'year'): string {
  return `${formatYearValue(year, unit)}${UNIT_SUFFIX[unit]}`;
}
